import { PublicClientApplication } from "@azure/msal-browser";

/**
 * Microsoft Entra (Azure AD) sign-in via MSAL.
 *
 * We use @azure/msal-browser directly (no msal-react) with a popup flow: the
 * SPA obtains an Entra ID token, then hands it to the backend
 * (POST /api/web/sessions/entra), which validates it and issues our own app
 * JWT — so nothing downstream of login changes.
 *
 * Authority defaults to /organizations (work/school accounts, multi-tenant).
 * Configure REACT_APP_ENTRA_CLIENT_ID (and optionally _AUTHORITY / _REDIRECT_URI).
 */

const clientId = process.env.REACT_APP_ENTRA_CLIENT_ID;

const msalConfig = {
  auth: {
    clientId: clientId,
    authority:
      process.env.REACT_APP_ENTRA_AUTHORITY ||
      "https://login.microsoftonline.com/organizations",
    redirectUri:
      process.env.REACT_APP_ENTRA_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

let instance = null;
let initPromise = null;

/** True when an Entra client id is configured (gates the sign-in button). */
export function isEntraConfigured() {
  return Boolean(clientId);
}

/**
 * Build the Microsoft admin-consent URL to hand a customer's IT admin so they
 * authorize this app inside their tenant. Uses the config's tenant id, or
 * /organizations when it's not known yet. After they consent, Microsoft
 * redirects back with `tenant=<theirTenantId>` in the URL — that's the value
 * for the config's Entra Tenant ID.
 *
 * @param {string} [tenantId]
 * @returns {string}
 */
export function buildAdminConsentUrl(tenantId) {
  const tenant =
    tenantId && String(tenantId).trim() ? String(tenantId).trim() : "organizations";
  const redirectUri =
    process.env.REACT_APP_ENTRA_REDIRECT_URI || window.location.origin;
  const params = new URLSearchParams({
    client_id: clientId || "",
    redirect_uri: redirectUri,
  });
  return `https://login.microsoftonline.com/${encodeURIComponent(
    tenant
  )}/adminconsent?${params.toString()}`;
}

function getInstance() {
  if (!instance) {
    instance = new PublicClientApplication(msalConfig);
  }
  if (!initPromise) {
    // msal-browser v3+ requires initialize() before any other API call.
    initPromise = instance.initialize();
  }
  return initPromise.then(() => instance);
}

/**
 * Run the Microsoft sign-in popup and resolve with the Entra ID token.
 *
 * @returns {Promise<string>} the raw ID token (JWT) to POST to the backend
 */
export async function loginWithMicrosoft() {
  const msal = await getInstance();
  const result = await msal.loginPopup({
    scopes: ["openid", "profile", "email"],
    prompt: "select_account",
  });
  return result.idToken;
}
